import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { businessService } from '@/services/business.service';
import toast from 'react-hot-toast';
import {
  Edit,
  Link2,
  PauseCircle,
  PlayCircle,
  PlusCircle,
  Plug2,
  RefreshCw,
  KeyRound,
  Code2,
} from 'lucide-react';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { LTI_INTEGRATION_PERMISSION } from '@/constants/constant-permissions';
import { getApiBaseUrl } from '@/config/api.config';
import {
  useLtiJwks,
  useLtiPlatforms,
  useActivateLtiPlatform,
  useSuspendLtiPlatform,
} from '@/hooks/useLti';
import { LtiPlatform, LtiPlatformStatus } from '@/types/lti.types';
import {
  ADMIN_LTI_ADD,
  ADMIN_LTI_EDIT,
} from '@/constants/constant-routes';
import CopyableInputField from '@/components/ui/CopyableInputField';
import Modal from '@/components/ui/Modal';
import ActivationConfirmationModal from '@/components/ui/ActivationConfirmationModal';
import UnmappedLtiLinks from './UnmappedLtiLinks';

export default function LtiIntegrations() {
  usePermissionRouteAccess([
    LTI_INTEGRATION_PERMISSION.ADMIN_LTI_INTEGRATION_READ,
    LTI_INTEGRATION_PERMISSION.ADMIN_LTI_INTEGRATION_WRITE,
  ]);
  const canWrite = useHasPermission([
    LTI_INTEGRATION_PERMISSION.ADMIN_LTI_INTEGRATION_WRITE,
  ]);
  const navigate = useNavigate();

  const { data: businessData } = useQuery({
    queryKey: ['businessSettings'],
    queryFn: () => businessService.getBusinessSettings(),
    staleTime: 5 * 60 * 1000,
  });
  const companyName = businessData?.data?.company_name?.trim() || businessData?.company_name?.trim() || 'Skillzen';

  const { data: platforms, isLoading, error } = useLtiPlatforms();
  const suspendMutation = useSuspendLtiPlatform();
  const activateMutation = useActivateLtiPlatform();

  const [showJwks, setShowJwks] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState<LtiPlatform | null>(null);
  const { data: jwks, isLoading: jwksLoading } = useLtiJwks();


  // In render this fires on every re-render while error is truthy, stacking
  // toasts. An effect fires once, when the error first arrives.
  useEffect(() => {
    if (!error) return;
    // 403 here usually means the browser now holds a learner session opened from an
    // LMS (e.g. Moodle), which can never manage LTI. Say so instead of "failed".
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 403) {
      toast.error(
        `Your current session can’t manage LTI integrations. This happens after opening ${companyName} from an LMS (such as Moodle) in this browser. Please log in to ${companyName} again.`,
        { id: 'lti-platforms-error', duration: 8000 }
      );
    } else {
      toast.error('Failed to load LTI platforms.', { id: 'lti-platforms-error' });
    }
  }, [error]);

  // The Tool URLs a customer pastes into their LMS when registering Skillzen.
  const base = getApiBaseUrl();
  const loginUrl = `${base}/lti/login`;
  const launchUrl = `${base}/lti/launch`;
  const jwksUrl = `${base}/.well-known/jwks.json`;

  const FeaturePill = ({ on, label }: { on: boolean; label: string }) => (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
        on ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-400'
      }`}
    >
      {label}
    </span>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-indigo-600 to-brand-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Plug2 className="w-8 h-8" />
          <h1 className="text-2xl font-bold">LTI Integrations</h1>
        </div>
        <p className="text-brand-indigo-100">
          Let customer LMS platforms (Moodle, Canvas, Cornerstone, …) launch {' '}
          {companyName} courses, certifications and learning paths via LTI 1.3.
        </p>
      </div>

      {/* Links an LMS has launched that nobody has mapped yet. Renders nothing
          when there are none, which is the normal state once Deep Linking is on. */}
      <UnmappedLtiLinks canWrite={canWrite} />

      {/* Tool Details — copy these into the LMS tool configuration */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-brand-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Your {companyName} Tool details
            </h2>
          </div>
          <button
            onClick={() => setShowJwks(true)}
            className="btn btn-outline btn-sm inline-flex items-center gap-2"
            title="View the public key set"
          >
            <KeyRound className="w-4 h-4" />
            View Public Keyset
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          When registering {companyName} as a tool inside an LMS, paste these URLs
          into the matching fields of the LMS tool configuration.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CopyableInputField
            label="OIDC Login / Initiation URL"
            value={loginUrl}
            helperText="LMS field: 'Initiate login URL' / 'Login URL'"
          />
          <CopyableInputField
            label="Launch / Redirection URL"
            value={launchUrl}
            helperText="LMS field: 'Redirection URI' / 'Launch URL'"
          />
          <CopyableInputField
            label="Public JWKS URL"
            value={jwksUrl}
            helperText="LMS field: 'Public keyset URL'"
          />
        </div>
      </div>

      {/* Actions bar */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          Registered Platforms
        </h2>
        <Link
          to={canWrite ? ADMIN_LTI_ADD : '#'}
          onClick={(e) => !canWrite && e.preventDefault()}
          className={`btn btn-primary inline-flex items-center gap-2${
            !canWrite ? ' opacity-50 cursor-not-allowed' : ''
          }`}
          title={!canWrite ? 'You do not have permission' : undefined}
        >
          <PlusCircle className="w-4 h-4" />
          Add Platform
        </Link>
      </div>

      {/* Platforms table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Issuer (LMS)
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Client ID
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Features
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="text-center py-8">
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Loading platforms...
                    </div>
                  </td>
                </tr>
              ) : !platforms || platforms.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    No LMS platforms registered yet. Click “Add Platform” to
                    register your first LMS.
                  </td>
                </tr>
              ) : (
                platforms.map((p: LtiPlatform) => (
                  <tr key={p._id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900 break-all">
                        {p.issuer}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded break-all">
                        {p.client_id}
                      </code>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-1 flex-wrap">
                        <FeaturePill on={p.ags_enabled} label="AGS" />
                        <FeaturePill on={p.deep_linking_enabled} label="Deep Link" />
                        <FeaturePill on={p.nrps_enabled} label="NRPS" />
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span
                        className={`badge ${
                          p.status === LtiPlatformStatus.ACTIVE
                            ? 'badge-success'
                            : 'badge-gray'
                        }`}
                      >
                        {p.status === LtiPlatformStatus.ACTIVE
                          ? 'Active'
                          : 'Suspended'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() =>
                            canWrite && navigate(`${ADMIN_LTI_EDIT}/${p._id}`)
                          }
                          className={`p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-50${
                            !canWrite ? ' opacity-50 cursor-not-allowed' : ''
                          }`}
                          title={
                            !canWrite ? 'You do not have permission' : 'Edit platform'
                          }
                          disabled={!canWrite}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {p.status === LtiPlatformStatus.SUSPENDED ? (
                          <button
                            onClick={() => canWrite && activateMutation.mutate(p._id)}
                            className={`p-2 text-gray-400 hover:text-green-600 rounded-md hover:bg-green-50${
                              !canWrite ? ' opacity-50 cursor-not-allowed' : ''
                            }`}
                            title={
                              !canWrite
                                ? 'You do not have permission'
                                : 'Re-activate platform — launches will be accepted again'
                            }
                            disabled={!canWrite || activateMutation.isPending}
                          >
                            <PlayCircle className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => canWrite && setSuspendTarget(p)}
                            className={`p-2 text-gray-400 hover:text-red-600 rounded-md hover:bg-red-50${
                              !canWrite ? ' opacity-50 cursor-not-allowed' : ''
                            }`}
                            title={
                              !canWrite ? 'You do not have permission' : 'Suspend platform'
                            }
                            disabled={!canWrite || suspendMutation.isPending}
                          >
                            <PauseCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Public Keyset (JWKS) modal */}
      <Modal
        isOpen={showJwks}
        onClose={() => setShowJwks(false)}
        title="Public Key Set (JWKS)"
        subtitle="Copy this into LMSs that require the raw key set instead of a URL."
        size="lg"
      >
        {jwksLoading ? (
          <div className="flex items-center gap-2 text-gray-500">
            <RefreshCw className="w-4 h-4 animate-spin" /> Loading keys...
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Code2 className="w-4 h-4" />
              {jwks?.keys?.length || 0} key(s) published
            </div>
            <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-4 overflow-auto max-h-96">
              {JSON.stringify(jwks || { keys: [] }, null, 2)}
            </pre>
            <button
              onClick={() => {
                navigator.clipboard.writeText(
                  JSON.stringify(jwks || { keys: [] }, null, 2),
                );
                toast.success('JWKS copied to clipboard!');
              }}
              className="btn btn-outline btn-sm"
            >
              Copy JWKS
            </button>
          </div>
        )}
      </Modal>

      <ActivationConfirmationModal
        isOpen={Boolean(suspendTarget)}
        onClose={() => setSuspendTarget(null)}
        onConfirm={() => {
          if (!suspendTarget) return;
          suspendMutation.mutate(suspendTarget._id, {
            onSettled: () => setSuspendTarget(null),
          });
        }}
        title="Suspend this LMS?"
        workflowName={suspendTarget?.issuer}
        description={
          'Every launch from this LMS will be rejected — learners already part-way through will ' +
          'not be able to reopen their content, and grades will stop being sent back. The ' +
          'registration, its deployments and every content mapping are kept — re-activate it ' +
          'from the same row and everything resumes.'
        }
        isActivating={false}
        isLoading={suspendMutation.isPending}
      />
    </div>
  );
}
